<template>
    <TransitionRoot as="template" :show="open">
        <Dialog
            as="div"
            static
            class="fixed z-10 inset-0 overflow-y-auto"
            @close="open = false"
            :open="open"
        >
            <div
                class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
            >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay
                        class="fixed inset-0 bg-opacity-75 transition-all duration-500 ease-in-out"
                        :class="{
                            'bg-gray-500': !warning,
                            'bg-red-300': warning,
                        }"
                    />
                </TransitionChild>

                <!-- This element is to trick the browser into centering the modal contents. -->
                <span
                    class="hidden sm:inline-block sm:align-middle sm:h-screen"
                    aria-hidden="true"
                    >&#8203;</span
                >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6"
                    >
                        <div
                            class="mx-auto flex items-center justify-center h-10 w-10 rounded-full fixed ml-auto top-0 right-0 cursor-pointer"
                            @click="open = false"
                        >
                            <XIcon
                                class="h-5 w-5 text-gray-900 right-0 hover:text-blue-700"
                            />
                        </div>
                        <div class="mt-2">
                            <div class="text-center sm:mt-0">
                                <div
                                    class="flex flex-row justify-between md:space-x-5"
                                >
                                    <div class="flex items-start space-x-5">
                                        <div class="flex-shrink-0">
                                            <div
                                                class="hidden relative rounded-full overflow-hidden lg:block"
                                            >
                                                <img
                                                    class="relative rounded-full w-20 h-20"
                                                    :alt="user.initials"
                                                    v-if="user.avatar"
                                                    :src="user.avatar"
                                                />
                                                <span
                                                    class="inline-flex items-center justify-center h-20 w-20 rounded-full bg-blue-500"
                                                    v-if="!user.avatar"
                                                >
                                                    <span
                                                        class="font-medium text-2xl leading-none text-white"
                                                        >{{
                                                            user.initials
                                                        }}</span
                                                    >
                                                </span>
                                                <span
                                                    class="absolute inset-0 shadow-inner rounded-full"
                                                    aria-hidden="true"
                                                />

                                                <label
                                                    for="logo"
                                                    class="absolute inset-0 w-full h-full bg-black bg-opacity-75 flex items-center justify-center text-sm font-medium text-white opacity-0 hover:opacity-100 focus-within:opacity-100"
                                                >
                                                    <span>
                                                        <PencilIcon
                                                            class="h-8 w-8 text-white inline hover:text-blue-50 cursor-pointer"
                                                            aria-hidden="true"
                                                        />
                                                    </span>

                                                    <input
                                                        id="logo"
                                                        v-show="!user.avatar"
                                                        ref="logo"
                                                        accept="image/*"
                                                        v-on:change="
                                                            handleFileUpload(
                                                                'logo'
                                                            )
                                                        "
                                                        type="file"
                                                        class="absolute inset-0 w-full h-full opacity-0 cursor-pointer border-gray-300 rounded-md"
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                        <div class="pt-1.5">
                                            <h1
                                                class="text-2xl font-bold text-gray-900"
                                            >
                                                {{ user.name }}
                                            </h1>
                                            <p
                                                class="text-sm font-medium text-gray-500 mt-1"
                                            >
                                                <span
                                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-blue-50"
                                                    >{{
                                                        t(
                                                            "meetings.tag.voting-weight",
                                                            "Stemgewicht:"
                                                        )
                                                    }}
                                                    {{
                                                        user.voting_weight
                                                    }}</span
                                                >

                                                <span
                                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2"
                                                    v-for="(
                                                        tag, id
                                                    ) in user.tags"
                                                    v-bind:key="id"
                                                    >{{ tag }}</span
                                                >
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div class="mt-2 text-left">
                                    <input class="opacity-0 w-4 h-4" />
                                </div>
                            </div>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import { ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { CheckIcon, PencilAltIcon, XIcon } from "@heroicons/vue/outline";

import { PencilIcon } from "@heroicons/vue/solid";

export default {
    components: {
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        CheckIcon,
        PencilAltIcon,
        PencilIcon,
        XIcon,
    },
    mounted() {
        this.getUser();
        Eventing.listen("closeModal", () => (this.open = false));

        Eventing.listen("noWarning", () => (this.warning = false));
        Eventing.listen("warning", () => (this.warning = true));

        Eventing.listen("openMyProfile", () => (this.open = true));
    },
    created() {},
    data() {
        const open = ref(false);

        return {
            open,
            user: null,
            warning: false,
        };
    },
    methods: {
        triggerFileUpload(field) {
            document.getElementById(field).click();
        },
        handleFileUpload(field) {
            this.file = this.$refs.logo.files[0];
            var self = this;
            let formData = new FormData();
            // formData.append("bestand", this.file);
            formData.append("photo", this.file);
            formData.append("photo-upload", true);

            axios({
                method: "post",
                url: "/api/avatar",
                data: formData,
            })
                .then(function (response) {
                    window.location.reload();
                })
                .catch((error) => {
                    console.error(error);
                    if (error.response) {
                        console.info(error.response.data);
                    }
                })
                .finally(function () {});
        },
        getUser() {
            var self = this;
            axios
                .post("/auth?view=profile")
                .then((response) => {
                    this.user = response.data.data;
                    this.open = true;
                })
                .catch((error) => {
                    // alert(error.response.status);
                });
        },
    },
};
</script>
